import Skeleton from "react-loading-skeleton";
import { KeyValuePairType } from "@repo/types/enums";
import { UpdateTicketField } from "../../types/enums/AddUpdateTicketField";
import type { KeyValuePairDTO } from "@repo/types/dtos";
import {
  KeyValueViewType,
  type KeyValueItem,
} from "@repo/types/ui/keyValuePairs";
import type { RecordId } from "@repo/types/ui/tables";
import {
  useGetKeyValuePairs,
  useGetTicketById,
  useAddUpdateTicket,
} from "../../hooks/queries";
import { useUserKeyValueItems } from "@repo/hooks/queries";
import {
  getKeyValueMenuButtonRenderer,
  getUserMenuButtonRenderer,
} from "./helpers/TicketFields";
import KeyValueMenu from "../ui/menu/KeyValueMenu";
import Input from "../form/input/InputField";
import TicketTimeLine from "./timelineActivity/TicketTimeLine";

export interface TicketOverviewProps {
  ticketId: RecordId;
}

export default function TicketOverview({ ticketId }: TicketOverviewProps) {
  const { data: userKeyValues } = useUserKeyValueItems();
  const { data: ticket } = useGetTicketById(ticketId);
  const { mutate: updateTicket } = useAddUpdateTicket();

  const getKeyValueItems = (
    keyValuePairs: KeyValuePairDTO[],
  ): KeyValueItem[] => {
    return keyValuePairs.map(
      (keyValuePair: KeyValuePairDTO): KeyValueItem => ({
        viewType: KeyValueViewType.textOnly,
        Id: keyValuePair.Id,
        Value: keyValuePair.Value,
        Description: keyValuePair.Description ?? "",
        PairType: keyValuePair.PairType ?? 0,
        PairTypeEnumValue: keyValuePair.PairTypeEnumValue ?? 0,
        BgColorHex: keyValuePair.BgColorHex,
        TextColorHex: keyValuePair.TextColorHex,
        SortOrder: keyValuePair.SortOrder ?? 0,
        classNames: "p-2 mb-2 justify-center",
      }),
    );
  };

  const { data: ticketStatusKeyValueItems } = useGetKeyValuePairs(
    KeyValuePairType.TicketStatus,
    getKeyValueItems,
  );

  const { data: ticketPriorityKeyValueItems } = useGetKeyValuePairs(
    KeyValuePairType.TicketPriority,
    getKeyValueItems,
  );

  return (
    <div
      className={
        "flex items-center justify-between overflow-scroll h-[calc(100vh-170px)] p-1 bg-gray-100"
      }
    >
      <div className="grid grid-rows-1 grid-cols-[1fr_3fr] w-[1366px] h-full gap-2 p-2">
        <div className="border border-gray-200 rounded-lg bg-white max-h-[calc(100vh-100px)] overflow-y-auto pb-8">
          <div className="flex items-center justify-between px-4 py-4 mb-4 border-b border-b-gray-200">
            <h2 className="text-lg font-medium">Ticket Info</h2>
          </div>

          <div className="row-start-2 col-start-1 p-4">
            <div className="grid grid-rows-8 grid-cols-1">
              <div className="">
                <label className="block text-medium text-gray-700">Title</label>
                {ticket ? <Input value={ticket.Title} /> : <Skeleton />}
              </div>

              <div className="">
                <label className="block text-medium text-gray-700">
                  Sent By
                </label>
                {ticket ? <Input value={ticket.FromName} /> : <Skeleton />}
              </div>

              <div className="">
                <label className="block text-medium text-gray-700">Owner</label>
                <div className="border border-gray-200 p-1 rounded">
                  {ticket && userKeyValues ? (
                    <KeyValueMenu
                      mode="assignee"
                      isMulti={true}
                      selectedKey={ticket.AssignedToId}
                      items={userKeyValues}
                      align="center"
                      onValueChange={async (newValue) => {
                        updateTicket({
                          Id: ticket.Id,
                          AssignedToId: newValue,
                          UpdateField: UpdateTicketField.AssignedToId,
                        });
                      }}
                      triggerRenderer={(selectedItem) =>
                        getUserMenuButtonRenderer(
                          userKeyValues ?? [],
                          selectedItem ?? ticket.AssignedToId,
                        )
                      }
                      maxHeight={300}
                    />
                  ) : (
                    <Skeleton />
                  )}
                </div>
              </div>

              <div className="h-10">
                <label className="block text-medium text-gray-700">
                  Status
                </label>

                {ticket && ticketStatusKeyValueItems ? (
                  <KeyValueMenu
                    selectedKey={ticket.StatusKeyValuePairId}
                    items={ticketStatusKeyValueItems}
                    align="center"
                    onValueChange={async (newValue) => {
                      updateTicket({
                        Id: ticket.Id,
                        StatusKeyValuePairId: newValue,
                        UpdateField: UpdateTicketField.Status,
                      });
                    }}
                    triggerRenderer={(selected) =>
                      getKeyValueMenuButtonRenderer(
                        ticketStatusKeyValueItems ?? [],
                        selected ?? ticket.StatusKeyValuePairId,
                      )
                    }
                    itemsEditable={true}
                    editText={"Edit Statuses"}
                    popupClassNames="p-2 w-55"
                    positionerSide="bottom"
                    positionerAlign="center"
                    maxHeight={300}
                  />
                ) : (
                  <Skeleton />
                )}
              </div>

              <div className="h-10">
                <label className="block text-medium text-gray-700">
                  Priority
                </label>

                {ticket && ticketPriorityKeyValueItems ? (
                  <KeyValueMenu
                    selectedKey={ticket.PriorityKeyValuePairId}
                    items={ticketPriorityKeyValueItems}
                    align="center"
                    onValueChange={async (newValue) => {
                      updateTicket({
                        Id: ticket.Id,
                        PriorityKeyValuePairId: newValue,
                        UpdateField: UpdateTicketField.Priority,
                      });
                    }}
                    triggerRenderer={(selected) =>
                      getKeyValueMenuButtonRenderer(
                        ticketPriorityKeyValueItems ?? [],
                        selected ?? ticket.PriorityKeyValuePairId,
                      )
                    }
                    itemsEditable={true}
                    editText={"Edit Priorities"}
                    popupClassNames="p-2 w-55"
                    positionerSide="bottom"
                    positionerAlign="center"
                    maxHeight={300}
                  />
                ) : (
                  <Skeleton />
                )}
              </div>

              <div className="">
                <label className="block text-medium text-gray-700">
                  Description
                </label>
                {ticket ? <Input value={ticket.Description} /> : <Skeleton />}
              </div>

              {ticket &&
                ticket.Attachments &&
                ticket.Attachments.length > 0 && (
                  <div className="">
                    <label className="block text-medium text-gray-700">
                      Attached Files
                    </label>
                    {/* <FileAttachments attachments={ticket.Attachments} /> */}
                  </div>
                )}

              <div className="">
                <label className="block text-medium text-gray-700">Email</label>

                {ticket ? <Input value={ticket.FromEmail} /> : <Skeleton />}
              </div>

              <div className="">
                <label className="block text-medium text-gray-700">
                  Ticket ID
                </label>

                {ticket ? <Input value={String(ticket.Id)} /> : <Skeleton />}
              </div>
            </div>
          </div>
        </div>

        <div className="border border-gray-200 rounded-lg bg-white">
          <TicketTimeLine ticketId={ticketId} />
        </div>
      </div>
    </div>
  );
}
